import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useIsMobile } from '@/hooks/use-mobile';

interface DadosMensais {
  mes: number;
  consumo: number;
  te: string;
  tusd: string;
  cosip: string;
  despesa_fatura: number;
  despesa_energia: number;
}

interface GeracaoMensal {
  [key: string]: number;
}

interface GeracaoConsumoChartProps {
  dadosMensais: DadosMensais[];
  geracaoMensal: GeracaoMensal;
  potenciaTotalPaineis: number;
  loading?: boolean;
}

const meses = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

// Mapeamento de números de mês para nomes em português
const mesesMap: { [key: number]: string } = {
  1: 'janeiro',
  2: 'fevereiro', 
  3: 'marco',
  4: 'abril',
  5: 'maio',
  6: 'junho',
  7: 'julho',
  8: 'agosto',
  9: 'setembro',
  10: 'outubro',
  11: 'novembro',
  12: 'dezembro'
};

export function GeracaoConsumoChart({ 
  dadosMensais, 
  geracaoMensal, 
  potenciaTotalPaineis,
  loading = false 
}: GeracaoConsumoChartProps) {
  const isMobile = useIsMobile();
  // Preparar dados para o gráfico
  const chartData = meses.map((mes, index) => {
    const mesNum = index + 1;
    const mesNome = meses[index];
    
    // Buscar dados de consumo
    const dadosMes = dadosMensais.find(d => d.mes === mesNum);
    const consumo = dadosMes?.consumo || 0;
    
    // Buscar dados de geração usando o nome do mês em português
    const mesNomePortugues = mesesMap[mesNum];
    const geracaoKwp = geracaoMensal[mesNomePortugues] || 0;
    
    // Calcular geração total em kWh (geracao_kwp × potencia_total_paineis)
    const geracao = geracaoKwp * potenciaTotalPaineis;
    
    return {
      mes: mesNome,
      geracao: Math.round(geracao * 100) / 100, // Arredondar para 2 casas decimais
      consumo: Math.round(consumo * 100) / 100
    };
  });

  // Adicionar linha de média
  const mediaGeracao = chartData.reduce((acc, item) => acc + item.geracao, 0) / 12;
  const mediaConsumo = chartData.reduce((acc, item) => acc + item.consumo, 0) / 12;
  
  chartData.push({
    mes: 'Média',
    geracao: Math.round(mediaGeracao * 100) / 100,
    consumo: Math.round(mediaConsumo * 100) / 100
  });

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            📊 Geração x Consumo
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Comparativo mensal de geração solar vs. consumo de energia
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className={`flex items-center justify-center ${isMobile ? 'h-64' : 'h-80'}`}>
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-muted-foreground text-sm">Carregando dados...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!dadosMensais.length || Object.keys(geracaoMensal).length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            📊 Geração x Consumo
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Comparativo mensal de geração solar vs. consumo de energia
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className={`flex items-center justify-center ${isMobile ? 'h-64' : 'h-80'}`}>
            <div className="text-center px-4">
              <p className="text-muted-foreground text-sm">
                Nenhum dado disponível para exibir o gráfico
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Selecione um cliente com dados de dimensionamento
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          📊 Geração x Consumo
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          Comparativo mensal de geração solar vs. consumo de energia
        </CardDescription>
      </CardHeader>
      <CardContent className={isMobile ? 'px-2 sm:px-4' : ''}>
        <div className={isMobile ? 'h-64' : 'h-80'}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: isMobile ? 10 : 20,
                right: isMobile ? 10 : 30,
                left: isMobile ? 5 : 20,
                bottom: isMobile ? 20 : 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="mes" 
                stroke="#9CA3AF"
                fontSize={isMobile ? 10 : 12}
                angle={isMobile ? -45 : 0}
                textAnchor={isMobile ? 'end' : 'middle'}
                height={isMobile ? 60 : 30}
              />
              <YAxis 
                stroke="#9CA3AF"
                fontSize={isMobile ? 10 : 12}
                width={isMobile ? 40 : 60}
                label={{ 
                  value: 'kWh', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fill: '#9CA3AF', fontSize: isMobile ? '10px' : '12px' }
                }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB',
                  fontSize: isMobile ? '12px' : '14px',
                  padding: isMobile ? '8px' : '12px'
                }}
                formatter={(value: number, name: string) => [
                  `${value} kWh`, 
                  name === 'geracao' ? 'Geração' : 'Consumo'
                ]}
                labelStyle={{ color: '#F9FAFB' }}
              />
              <Legend 
                wrapperStyle={{ color: '#F9FAFB', fontSize: isMobile ? '12px' : '14px' }}
              />
              <Bar 
                dataKey="geracao" 
                fill="#F59E0B" 
                name="Geração"
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="consumo" 
                fill="#1E40AF" 
                name="Consumo"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        {/* Informações adicionais */}
        <div className={`mt-4 grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <p className={`font-medium text-muted-foreground ${isMobile ? 'text-xs' : 'text-sm'}`}>Geração Total Anual</p>
            <p className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-amber-600`}>
              {Math.round(chartData.slice(0, 12).reduce((acc, item) => acc + item.geracao, 0))} kWh
            </p>
          </div>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <p className={`font-medium text-muted-foreground ${isMobile ? 'text-xs' : 'text-sm'}`}>Consumo Total Anual</p>
            <p className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-blue-600`}>
              {Math.round(chartData.slice(0, 12).reduce((acc, item) => acc + item.consumo, 0))} kWh
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
